import { formatDateTimePipe } from "@/core/pipes/format-date.pipe"
import { useUpdate } from "@/core/hooks/use-update.hook"
import { App, Button, Empty, InputNumber, message, Modal, Spin, Tour, TourProps, Checkbox, Alert } from "antd"
import { waterIndexStatusTagPipe } from "@/core/pipes/water-index-status.pipe"
import { CheckCircleOutlined, CheckOutlined, CloseOutlined, EditOutlined, LeftOutlined, RightOutlined, UndoOutlined } from "@ant-design/icons"
import { WaterIndexModel } from "@/core/models/water-index.model"
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo"
import container from "@/lib/di/container"
import { useState, useEffect, KeyboardEvent, useRef } from "react"
import { FilterInputTemplate, FilterInputType } from "@/app/home/components/filter/FilterBar"
import { FilterSearchPayload } from "@/app/home/components/table/DataTable"
import { WaterIndexStatus } from "@/core/enums/global.enums"

const waterIndexRepo = container.get(WaterIndexRepo);

interface ImageModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filterMap: Map<string, FilterSearchPayload>;
  defaultParams?: object;
  index: number;
}

export const ApprovalModal: React.FC<ImageModalProps> = ({ isModalOpen, setIsModalOpen, filterMap, index, defaultParams }) => {
    const [page, setPage] = useState(index);
    const [total, setTotal] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [isProcessing, setProcessing] = useState(false);
    const [data, setData] = useState<WaterIndexModel>();
    const [previousMonthData, setPreviousMonthData] = useState<WaterIndexModel | null>(null);
    const [isLoadingPreviousMonth, setIsLoadingPreviousMonth] = useState(false);
    const [waterMeterNumber, setWaterMeterNumber] = useState(data?.waterMeterNumber);
    const [isFullRotation, setIsFullRotation] = useState(false);
    const [isLargeAmount, setIsLargeAmount] = useState(false);
    const [isReverseRotated, setIsReverseRotated] = useState(false);
    const {modal} = App.useApp();
    //* Reference used for tour */
    const backRef = useRef(null);
    const forwardRef = useRef(null);
    const actionRef = useRef(null);
    const resetRef = useRef(null);
    const formRef = useRef(null);
    const [tour, setTour] = useState<boolean>(false);
    const steps: TourProps['steps'] = [
        {
            title: 'Duyệt chỉ số nước',
            description: 'So sánh và duyệt hoặc chỉnh sửa chỉ số nước theo ảnh chụp. Bạn có thể chỉnh sửa chỉ số ở ô này để chuyển sang chế độ chỉnh sửa chỉ số.',
            target: () => formRef.current,
        },
        {
            title: 'Duyệt/ chỉnh sửa chỉ số nước',
            description: 'Duyệt/ chỉnh sửa chỉ số nước bằng cách ấn nút hoặc sử dụng phím [Enter].',
            target: () => actionRef.current,
        },
        {
            title: 'Đặt lại chỉ số đã sửa',
            description: 'Nếu bạn sửa chỉ số không như mong muốn. Bạn có thể ấn nút để đưa chỉ số về trạng thái ban đầu.',
            target: () => resetRef.current,
        },
        {
            title: 'Chuyển đến chỉ số tiếp theo',
            description: 'Có thể sử dụng nút ấn hoặc phím [.](Dấu chấm) để chuyển đến chỉ số tiếp theo.',
            target: () => forwardRef.current,
        },
        {
            title: 'Quay lại chỉ số trước',
            description: 'Có thể sử dụng nút ấn hoặc phím [,](Dấu phẩy) để quay lại chỉ số trước đó.',
            target: () => backRef.current,
        },
      ];

    useEffect(() => {
        setPage(index);
    }, [index]);

    const getData = async () => {
        setLoading(true);
        let query: any = {};
        filterMap.forEach(filter => {
            if (filter.value) {
                query[filter.key] = filter.value
            }
        })
        if (defaultParams) {
            query = {...query, ...defaultParams };
        }
        const result = await waterIndexRepo.getData(page, query, 1);
        if (result) {
            setTotal(result.total);
            if (result.data[0]) {
                const currentData = result.data[0];
                setWaterMeterNumber(currentData?.waterMeterNumber);
                setIsFullRotation(currentData?.isFullRotation ?? false);
                setIsLargeAmount(false);
                setIsReverseRotated(false);
                
                // Lấy chi tiết để có previousIndexes
                setIsLoadingPreviousMonth(true);
                try {
                    const detailData = await waterIndexRepo.getDetail(currentData.id);
                    if (detailData) {
                        // Merge waterUser từ currentData vào detailData để đảm bảo thông tin khách hàng được hiển thị
                        const mergedData = {
                            ...detailData,
                            waterUser: detailData.waterUser || currentData.waterUser
                        };
                        setData(mergedData);
                        
                        // Lấy tháng trước từ previousIndexes (tháng gần nhất trong mảng)
                        if (detailData.previousIndexes && detailData.previousIndexes.length > 0) {
                            // Tính toán tháng/năm trước
                            let prevMonth = currentData.month - 1;
                            let prevYear = currentData.year;
                            if (prevMonth < 1) {
                                prevMonth = 12;
                                prevYear = currentData.year - 1;
                            }
                            
                            // Tìm tháng trước trong previousIndexes (lấy tháng gần nhất)
                            const prevData = detailData.previousIndexes.find(
                                item => item.year === prevYear && item.month === prevMonth
                            );
                            
                            if (prevData) {
                                // Convert WaterIndexHistory thành WaterIndexModel format
                                setPreviousMonthData({
                                    id: prevData.id,
                                    year: prevData.year,
                                    month: prevData.month,
                                    waterUserId: prevData.waterUserId,
                                    waterMeterNumber: prevData.waterMeterNumber ?? undefined,
                                    waterMeterCode: currentData.waterMeterCode,
                                    image: prevData.image || "",
                                    createdAt: prevData.createdAt ? new Date(prevData.createdAt) : undefined,
                                    createdBy: "",
                                    updatedAt: undefined,
                                    updatedBy: "",
                                    waterFactoryId: currentData.waterFactoryId,
                                    tollAreaId: currentData.tollAreaId || "",
                                    waterUser: currentData.waterUser,
                                    note: prevData.note || "",
                                    latitude: currentData.latitude,
                                    longtitude: currentData.longtitude,
                                    oldNumber: prevData.oldNumber ?? undefined,
                                    useAmount: prevData.useAmount ?? undefined,
                                    status: undefined,
                                } as WaterIndexModel);
                            } else {
                                setPreviousMonthData(null);
                            }
                        } else {
                            setPreviousMonthData(null);
                        }
                    } else {
                        setData(currentData);
                        setPreviousMonthData(null);
                    }
                } catch (error) {
                    console.log(error);
                    setData(currentData);
                    setPreviousMonthData(null);
                }
                setIsLoadingPreviousMonth(false);
            } else {
                setWaterMeterNumber(undefined);
                setData(undefined);
                setPreviousMonthData(null);
                setIsFullRotation(false);
                setIsLargeAmount(false);
                setIsReverseRotated(false);
            }
        }
        setLoading(false);
    }

    useEffect(()=>{
        getData();
    }, [page])

    useUpdate(()=>{
        setPage(1);
        getData();
    }, [filterMap])
    
    function onKey(event: KeyboardEvent<HTMLInputElement>) {
        if (!isModalOpen) return;
        if (['190', '.'].includes(event.key)) {
            if (!isProcessing&&!isLoading) {
                event.preventDefault()
                setPage(page < total?page+1:total);
            }
        }
        if (['188', ','].includes(event.key)) {
            if (!isProcessing&&!isLoading) {
                event.preventDefault()
                setPage(page > 1?page-1:1);
            }
        }
        if (['13', 'Enter'].includes(event.key)) {
            if (!isProcessing&&!isLoading) {
                event.preventDefault()
                updateIndex();
            }
        }
    }

    async function disapproveIndex() {
        Modal.confirm({
            title: `Bạn có chắc chắn muốn hủy duyệt`,
            content: `Bạn có chắc chắn muốn hủy duyệt chỉ số nước tháng ${data?.month}/${data?.year} của khách hàng ${data?.waterUser?.name} - ${data?.waterUser?.code}?`,
            okText: "Hủy duyệt",
            onOk: async () => {
                setProcessing(true);
                if (!data) return;
                const result = await waterIndexRepo.disapproveOne(data);
                if (result === true) {
                    getData();
                    message.success("Hủy duyệt thành công");
                } else {
                    getData();
                    message.error(typeof result === "string"?result:"Hủy duyệt thất bại");
                }
                setProcessing(false);
            },
        });
        
    }

    async function updateIndex() {
        setProcessing(true);
        if (!data) return;
        const model: WaterIndexModel = {...data};
        if (model.waterMeterNumber != waterMeterNumber) {
            const confirmed = await modal.confirm({
                title: "Bạn có chắc chắn muốn sửa chỉ số nước?",
                content: <div>
                    <p>Chỉ số nước hiện tại: {model.waterMeterNumber}</p>
                    <p>Chỉ số nước mới: {waterMeterNumber}</p>
                </div>,
                okText: "Xác nhận",
                cancelText: "Huỷ"
            });
            if (confirmed) {
                model.waterMeterNumber = waterMeterNumber;
                const result = await waterIndexRepo.updateIndex(model);
                setData(model);
                if (result?.code === "00") {
                    message.success({
                        content: "Đã cập nhật chỉ số"
                    })
                } else {
                    message.error({
                        content: result?.errorMessage??"Cập nhật chỉ số thất bại"
                    })
                }
            }
        } else {
            // Tính toán số lượng sử dụng
            const useAmount = (waterMeterNumber ?? 0) - (data.oldNumber ?? 0);
            
            // Kiểm tra đồng hồ quay ngược: chỉ số mới < chỉ số cũ
            const isReverseDetected = (waterMeterNumber ?? 0) < (data.oldNumber ?? 0);
            
            // Validation theo các quy tắc mới:
            // 1. Nếu useAmount > 5000: Bắt buộc phải tích cờ "Chỉ số lớn" HOẶC cờ "Đồng hồ quay ngược"
            if (useAmount > 5000) {
                if (!isLargeAmount && !isReverseRotated) {
                    message.warning("Số lượng sử dụng lớn hơn 5000 khối. Vui lòng tích cờ 'Chỉ số lớn (> 2000 khối)' HOẶC cờ 'Đồng hồ quay ngược' trước khi duyệt.");
                    setProcessing(false);
                    return;
                }
            }
            
            // 2. Nếu useAmount < -5000: Bắt buộc phải tích cờ "Đồng hồ đã quay hết 1 vòng"
            if (useAmount < -5000) {
                if (!isFullRotation) {
                    message.warning("Số lượng sử dụng nhỏ hơn -5000 khối. Vui lòng tích cờ 'Đồng hồ đã quay hết 1 vòng' trước khi duyệt.");
                    setProcessing(false);
                    return;
                }
            }
            
            // 3. Nếu -1000 < useAmount < 0: Bắt buộc phải tích cờ "Đồng hồ bị quay ngược"
            if (useAmount < 0 && useAmount > -1000) {
                if (!isReverseRotated) {
                    message.warning("Đồng hồ đã quay ngược (số lượng sử dụng âm). Vui lòng tích cờ 'Đồng hồ bị quay ngược' trước khi duyệt.");
                    setProcessing(false);
                    return;
                }
            }
            
            // Chỉ gửi cờ khi người dùng tích
            const shouldSendLargeAmount = isLargeAmount ? true : null;
            const shouldSendFullRotation = isFullRotation ? true : null;
            const shouldSendReverseRotated = isReverseRotated ? true : null;
            
            const result = await waterIndexRepo.approve(model, shouldSendLargeAmount, shouldSendFullRotation, shouldSendReverseRotated);
            const isFilterAll = filterMap.get("indexStatus")?.value !== WaterIndexStatus.COMPLETED;
            if (result === true) {
                if (isFilterAll) {
                    setPage(page+1)
                } else {
                    getData();
                }
                message.success("Duyệt thành công");
            } else {
                getData();
                message.error(typeof result === "string"?result:"Duyệt thất bại");
            }
        }
        setProcessing(false);
    }
    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleOk = () => {
        setIsModalOpen(false);
    };

    const handleCancel = () => {
        waterIndexRepo.refresh();
        setIsModalOpen(false);
    };

    const renderButtons = (data: WaterIndexModel) => {
        switch (data.status) {
            case WaterIndexStatus.CONFIRMED:
                return (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-row items-center justify-center border-2 rounded p-4 border-gray-200 bg-white">
                            <p className="text-sm text-gray-600 mr-3">Chỉ số hiện tại:</p>
                            <InputNumber 
                                controls={false} 
                                value={waterMeterNumber} 
                                variant="borderless"
                            ref={formRef}
                            style={{ 
                                textAlignLast: 'center',
                                    fontSize: 32,
                                    width: 200,
                                fontWeight: "bold",
                            }}
                            disabled
                            type="number"
                            />
                    </div>
                    <Button
                        loading={isProcessing}
                        type="primary" 
                            icon={<CloseOutlined />} 
                        onClick={() => disapproveIndex()}
                            size="large"
                        style={{
                            background: "var(--danger-color)",
                                height: 50,
                            }}
                        >
                            Hủy duyệt
                        </Button>
                    </div>
                );
                case WaterIndexStatus.COMPLETED:
                return (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2">
                            <p className="text-sm text-gray-600 font-semibold">Nhập chỉ số nước:</p>
                            <div className="flex flex-row items-center justify-center border-2 rounded p-4 border-blue-300 bg-white">
                                <InputNumber 
                                    controls={false} 
                                    value={waterMeterNumber} 
                                    variant="borderless"
                                ref={formRef}
                                style={{
                                    textAlignLast: 'center',
                                        fontSize: 36,
                                        width: 250,
                                    fontWeight: "bold",
                                }}
                                onChange={(value) => setWaterMeterNumber(value??undefined)}
                                autoFocus
                                onKeyDown={onKey}
                                type="number"
                                />
                            </div>
                            {data.oldNumber !== null && data.oldNumber !== undefined && (
                                <div className="flex flex-row items-center justify-between text-sm">
                                    <span className="text-gray-600">Chỉ số cũ: <strong>{data.oldNumber}</strong></span>
                                    <span className="text-gray-600">
                                        Số lượng sử dụng: <strong className="text-primary">{(waterMeterNumber ?? 0) - (data.oldNumber ?? 0)} m³</strong>
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row gap-2">
                        <Button
                            ref={actionRef}
                            loading={isProcessing}
                            type="primary" 
                                icon={data.waterMeterNumber === waterMeterNumber ? <CheckCircleOutlined /> : <EditOutlined />} 
                            onClick={() => updateIndex()}
                                size="large"
                            style={{
                                    background: (data.waterMeterNumber === waterMeterNumber) ? "var(--success-button)" : "var(--danger-color)",
                                    flex: 1,
                                    height: 50,
                                }}
                            >
                                {data.waterMeterNumber === waterMeterNumber ? "Phê duyệt" : "Sửa chỉ số"} (Enter)
                            </Button>
                        <Button
                            ref={resetRef}
                            loading={isProcessing}
                            type="default"
                            icon={<UndoOutlined />}
                            onClick={() => setWaterMeterNumber(data.waterMeterNumber)}
                                size="large"
                                style={{ height: 50 }}
                            disabled={data.waterMeterNumber === waterMeterNumber}
                            >
                                Huỷ
                            </Button>
                        </div>
                    </div>
                );
                default: return <></>
        }           
    }

    return (
        <Modal
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            centered
            width={1400}
            footer={[]}
        >
            <div className="flex flex-row w-full items-center justify-start select-none">
                <p className="ml-3 text-2xl font-bold text-primary">DUYỆT CHỈ SỐ NƯỚC</p>
                <Button size="small" className="ml-2" type="default" shape="circle" onClick={()=>setTour(true)}>?</Button>
            </div>
            <div className='flex flex-col justify-center items-center'>
                {data ? (
                    <div className="w-full">
                        {isLoading ? (
                            <div className="w-[450px] h-[650px] mt-4 flex flex-row justify-center items-center">
                        <Spin></Spin>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 mt-4 gap-4 px-4">
                                {/* Cột 1: Ảnh tháng trước */}
                                <div className="flex flex-col">
                                    <div className="flex flex-row gap-2 mb-2">
                                        {previousMonthData ? (
                                            <p className="text-sm font-semibold text-gray-600">Tháng trước ({previousMonthData.month}/{previousMonthData.year})</p>
                                        ) : isLoadingPreviousMonth ? (
                                            <div className="flex flex-row items-center gap-2">
                                                <Spin size="small" />
                                                <p className="text-sm text-gray-500">Đang tải...</p>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400">Không có dữ liệu</p>
                                        )}
                                    </div>
                                    <div className="w-full h-[650px] border border-gray-200 rounded">
                                        {previousMonthData?.image ? (
                                            <img className="w-full h-full object-contain" src={previousMonthData.image} alt="Chỉ số nước tháng trước" />
                                        ) : isLoadingPreviousMonth ? (
                                            <div className="flex flex-row items-center justify-center h-full">
                                                <Spin />
                                            </div>
                                        ) : (
                                            <div className="flex flex-row items-center justify-center h-full">
                                                <Empty description="Không có ảnh" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cột 2: Ảnh tháng hiện tại */}
                                <div className="flex flex-col">
                                    <div className="flex flex-row gap-2 mb-2">
                                        <p className="text-sm font-semibold text-gray-600">Tháng hiện tại ({data.month}/{data.year})</p>
                                    </div>
                                    <div className="w-full h-[650px] border border-gray-200 rounded">
                                        {data.image ? (
                                            <img className="w-full h-full object-contain" src={data.image} alt="Chỉ số nước tháng hiện tại" />
                                        ) : (
                                            <div className="flex flex-row items-center justify-center h-full">
                                                <Empty description="Ảnh không tồn tại!" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Cột 3: Thông tin và duyệt */}
                                <div className="flex flex-col gap-4">
                                    {/* Thông tin khách hàng */}
                                    <div className="bg-gray-50 p-4 rounded">
                                        <p className="text-lg font-bold mb-3 text-primary">THÔNG TIN KHÁCH HÀNG</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-sm text-gray-600">Mã hợp đồng:</p>
                                                <p className="text-base font-semibold">{data.waterUser?.code ?? ""}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Đại diện hợp đồng:</p>
                                                <p className="text-base font-semibold">{data.waterUser?.name ?? ""}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Mã đồng hồ:</p>
                                                <p className="text-base font-semibold">{data?.waterMeterCode ?? ""}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Người đo:</p>
                                                <p className="text-base">{data.createdBy} - {data.createdAt ? formatDateTimePipe(data.createdAt as any) : "Chưa cập nhật"}</p>
                                            </div>
                                            {previousMonthData && (
                                                <>
                                                    <div>
                                                        <p className="text-sm text-gray-600">Chỉ số tháng trước:</p>
                                                        <p className="text-base font-semibold">{previousMonthData.waterMeterNumber ?? "N/A"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-600">Ngày đo tháng trước:</p>
                                                        <p className="text-base">{previousMonthData.createdAt ? formatDateTimePipe(previousMonthData.createdAt as any) : "N/A"}</p>
                                                    </div>
                                                </>
                                            )}
                                            <div className="col-span-2">
                                                <p className="text-sm text-gray-600 mb-1">Trạng thái:</p>
                                {waterIndexStatusTagPipe(data.status, {
                                                    padding: "4px 16px",
                                                    fontSize: 14
                                })}
                            </div>
                                        </div>
                                    </div>

                                    {/* Phần cảnh báo và checkbox */}
                                    <div className="flex flex-col gap-3">
                                        {data.isReverseRotated === true && (
                                            <Alert
                                                message="Đồng hồ quay ngược"
                                                description={
                                                    <div>
                                                        <p>Đồng hồ đã quay ngược do áp suất ngược.</p>
                                                        {data.useAmount !== null && data.useAmount !== undefined ? (
                                                            <p className="mt-1"><strong>Số lượng sử dụng:</strong> {data.useAmount} m³</p>
                                                        ) : (
                                                            <p className="mt-1 text-orange-600">Số lượng sử dụng chưa được tính toán.</p>
                                                        )}
                                                        {data.waterMeterMaxIndex && (
                                                            <p className="mt-1"><strong>Chỉ số tối đa:</strong> {data.waterMeterMaxIndex}</p>
                                                        )}
                                                    </div>
                                                }
                                                type="warning"
                                                showIcon
                                            />
                                        )}
                            {(() => {
                                const useAmount = (waterMeterNumber ?? 0) - (data.oldNumber ?? 0);
                                
                                // Kiểm tra đồng hồ quay ngược: chỉ số mới < chỉ số cũ
                                const isReverseDetected = (waterMeterNumber ?? 0) < (data.oldNumber ?? 0);
                                
                                // Các điều kiện hiển thị checkbox
                                const showLargeAmountCheckbox = useAmount > 2000;
                                // Chỉ hiển thị checkbox "Đồng hồ đã quay hết 1 vòng" khi:
                                // - useAmount < -5000 (bắt buộc)
                                // - isReverseDetected nhưng KHÔNG thuộc trường hợp -1000 < useAmount < 0 (vì trường hợp này dùng cờ "Đồng hồ bị quay ngược")
                                // - KHÔNG hiển thị khi useAmount > 5000 (vì trường hợp này dùng cờ "Đồng hồ quay ngược")
                                const showFullRotationCheckbox = useAmount < -5000 || (isReverseDetected && !(useAmount < 0 && useAmount > -1000) && useAmount <= 5000);
                                // Hiển thị checkbox "Đồng hồ quay ngược" khi:
                                // - -1000 < useAmount < 0 (bắt buộc)
                                // - useAmount > 5000 (bắt buộc, cùng với chỉ số lớn)
                                const showReverseRotatedCheckbox = (useAmount < 0 && useAmount > -1000) || useAmount > 5000;
                                const requireLargeAmountOrFullRotation = useAmount > 5000;
                                
                                return (
                                    <>
                                        {showLargeAmountCheckbox && data.status === WaterIndexStatus.COMPLETED && (
                                            <div className="mb-4">
                                                <Alert
                                                    message={requireLargeAmountOrFullRotation ? "Số lượng sử dụng lớn hơn 5000 khối (BẮT BUỘC)" : "Số lượng sử dụng lớn hơn 2000 khối"}
                                                    description={
                                                        requireLargeAmountOrFullRotation 
                                                            ? `Số lượng sử dụng: ${useAmount} m³. BẮT BUỘC phải tích cờ 'Chỉ số lớn' HOẶC cờ 'Đồng hồ quay ngược'.`
                                                            : `Số lượng sử dụng: ${useAmount} m³. Vui lòng tích cờ bên dưới để xác nhận.`
                                                    }
                                                    type={requireLargeAmountOrFullRotation ? "error" : "warning"}
                                                    showIcon
                                                    className="mb-2"
                                                />
                                                <Checkbox
                                                    checked={isLargeAmount}
                                                    onChange={(e) => setIsLargeAmount(e.target.checked)}
                                                >
                                                    Chỉ số lớn ({'>'} 2000 khối)
                                                </Checkbox>
                                            </div>
                                        )}
                                        {showReverseRotatedCheckbox && data.status === WaterIndexStatus.COMPLETED && (
                                            <div className="mb-4">
                                                {(() => {
                                                    if (useAmount > 5000) {
                                                        return (
                                                            <Alert
                                                                message="Số lượng sử dụng lớn hơn 5000 khối (BẮT BUỘC)"
                                                                description={`Số lượng sử dụng: ${useAmount} m³. BẮT BUỘC phải tích cờ 'Chỉ số lớn' HOẶC cờ 'Đồng hồ quay ngược'.`}
                                                                type="error"
                                                                showIcon
                                                                className="mb-2"
                                                            />
                                                        );
                                                    } else if (useAmount < 0 && useAmount > -1000) {
                                                        return (
                                                            <Alert
                                                                message="Đồng hồ quay ngược (BẮT BUỘC)"
                                                                description={`Số lượng sử dụng: ${useAmount} m³ (âm). BẮT BUỘC phải tích cờ 'Đồng hồ quay ngược'.`}
                                                                type="error"
                                                                showIcon
                                                                className="mb-2"
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                <Checkbox
                                                    checked={isReverseRotated}
                                                    onChange={(e) => setIsReverseRotated(e.target.checked)}
                                                >
                                                    Đồng hồ quay ngược
                                                </Checkbox>
                                            </div>
                                        )}
                                        {showFullRotationCheckbox && data.status === WaterIndexStatus.COMPLETED && (
                                            <div className="mb-4">
                                                {(() => {
                                                    if (useAmount < -5000) {
                                                        return (
                                                            <Alert
                                                                message="Số lượng sử dụng nhỏ hơn -5000 khối (BẮT BUỘC)"
                                                                description={`Số lượng sử dụng: ${useAmount} m³. BẮT BUỘC phải tích cờ 'Đồng hồ đã quay hết 1 vòng'.`}
                                                                type="error"
                                                                showIcon
                                                                className="mb-2"
                                                            />
                                                        );
                                                    } else if (isReverseDetected) {
                                                        return (
                                                            <Alert
                                                                message="Đồng hồ quay ngược"
                                                                description={`Chỉ số mới (${waterMeterNumber ?? 0}) nhỏ hơn chỉ số cũ (${data.oldNumber ?? 0}). Vui lòng tích cờ bên dưới để xác nhận đồng hồ đã quay hết 1 vòng.`}
                                                                type="error"
                                                                showIcon
                                                                className="mb-2"
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                                <Checkbox
                                                    checked={isFullRotation}
                                                    onChange={(e) => setIsFullRotation(e.target.checked)}
                                                >
                                                    Đồng hồ đã quay hết 1 vòng
                                                </Checkbox>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                                    </div>
                                    
                                    {/* Phần nhập chỉ số và nút duyệt */}
                                    <div className="bg-blue-50 p-4 rounded border border-blue-200">
                                        <p className="text-lg font-bold mb-3 text-primary">DUYỆT CHỈ SỐ</p>
                            {renderButtons(data)}
                        </div>
                                </div>
                            </div>
                        )}
                </div>
                ) : (
                    <div className="w-full h-[400px] flex flex-row items-center justify-center px-4">
                    <Empty />
                    </div>
                )}
                <div className="pt-4 w-full px-4">
                    <div className="flex flex-row justify-center">
                        <Button ref={backRef} icon={<LeftOutlined />} onClick={()=>setPage(page > 1?page-1:1)}>Quay lại</Button>
                        <div className="flex flex-row items-center justify-center border px-4 rounded mx-4">
                            {isLoading?<Spin />:<p>{page} / {total}</p>}
                        </div>
                        <div className="flex-1"></div>
                        <Button ref={forwardRef} icon={<RightOutlined />} iconPosition="end" onClick={()=>setPage(page < total?page+1:total)}>Tiếp theo</Button>
                </div>
            </div>
            </div>
            <Tour open={tour} onClose={() => setTour(false)} steps={steps} />
        </Modal>
    );
};

